// PAR package transitions: when the default count package materially changes,
// what happens to the PAR?
//
// The normalized PAR belongs to the ITEM (parAndMoq.mjs) and must never move
// because a vendor or package changed. But a PAR that was TYPED as "1 Sysco
// case" reads strangely once the shelf holds US Foods cases, and silently
// converting it to "1 US Foods case" would cut the stocking target by a third.
// So the app detects the transition, explains the exact consequence, and asks
// — once per transition, at the natural point of use.
//
// This module is the pure half: detection, the deterministic recommendation,
// and the fact set handed to the AI. It does NO unit conversion — callers
// supply package quantities already resolved in the item's count unit (the
// edge side from packageLadder.interpretPackage, the browser from its ladder
// rungs) — so it is one implementation for both runtimes, imported directly
// (same pattern as vendorState.mjs; do not add a browser copy).
//
// Hard rules, enforced here and by the callers:
//   * A recommendation is a suggestion. Nothing here changes a PAR.
//   * Conversions are inputs, never outputs — the AI cannot state one.
//   * An unresolved conversion never guesses: the transition reports
//     needs_review and the PAR stays exactly where it was.
//
// Pure ESM. No Deno APIs, no I/O.

const round6 = (n) => Math.round(n * 1e6) / 1e6;

const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const finitePositiveOrNull = positive;

// Close enough to count as "the same amount": 0.1% — package definitions are
// exact numbers, so anything beyond float noise is a real change.
const EQUIVALENT_TOLERANCE = 0.001;
// A whole number of packages, allowing for 6dp storage rounding.
const WHOLE_TOLERANCE = 1e-4;

export const PAR_TRANSITION_REASONS = Object.freeze({
  NO_PAR: "no_par",
  PAR_IN_COUNT_UNIT: "par_in_count_unit",
  NO_PACKAGE_DEFAULT: "no_package_default",
  SAME_PACKAGE: "same_package",
  EQUIVALENT_PACKAGE: "equivalent_package",
  EXACT_EQUIVALENT: "exact_equivalent",
  UNRESOLVED_CONVERSION: "unresolved_conversion",
});

/**
 * The two things a user can decide, in operational terms:
 *   keep_quantity          — keep the same stock level (normalized PAR
 *                            unchanged; the display re-reads in new packages)
 *   preserve_package_count — keep the same NUMBER of packages (1 old case →
 *                            1 new case), which intentionally moves the
 *                            normalized PAR to the new package size and
 *                            always requires explicit confirmation.
 * The AI may also decline with ask_user_without_recommendation.
 */
export const PAR_DECISIONS = Object.freeze({
  KEEP: "keep_quantity",
  PRESERVE_PACKAGE_COUNT: "preserve_package_count",
  ASK: "ask_user_without_recommendation",
});

/**
 * The one tunable that decides when the PAR-INTENT AI is consulted (never
 * what the arithmetic says, never which choices exist, never whether
 * confirmation is required): when preserving the package count would move the
 * normalized PAR by at least this fraction, the safe recommendation — keep
 * the same stock level — is obvious and no API call is spent. Below it, the
 * intent is genuinely ambiguous and the AI may weigh verified history.
 * Adjust after staging observation.
 */
export const PAR_INTENT_AI_THRESHOLD = 0.2;

/**
 * Detects whether a PAR prompt is due for one item.
 *
 * @param {object} args
 * @param {number|null} args.normalizedPar    the resolved normalized PAR, count unit
 * @param {{value:number|null, unit:string|null, optionId:*}|null} args.parDisplay
 *        how the PAR was entered (resolveRowPar().display)
 * @param {number|null} args.parQtyPerPackage the PAR package's contents at entry
 *        time (par_conversion_snapshot.quantity_per_package), if known
 * @param {*}      args.newOptionId          the current default count package
 * @param {number|null} args.newQtyPerPackage its contents in the count unit;
 *        null = unresolved
 * @param {string} [args.newPackageLabel]
 * @param {string} [args.oldPackageLabel]
 * @param {string} [args.countUnit]
 * @returns {{ required:boolean, reason:string|null, needsReview:boolean,
 *             transition:object|null }}
 */
export function detectParTransition({
  normalizedPar,
  parDisplay,
  parQtyPerPackage,
  newOptionId,
  newQtyPerPackage,
  newPackageLabel,
  oldPackageLabel,
  countUnit,
} = {}) {
  const no = (reason, needsReview = false) => ({
    required: false,
    reason,
    needsReview,
    transition: null,
  });

  const par = finitePositiveOrNull(normalizedPar);
  if (par === null) return no(PAR_TRANSITION_REASONS.NO_PAR);

  const parOptionId = parDisplay?.optionId ?? null;
  // A PAR entered directly in the item's own unit has nothing to transition:
  // "12 bags" reads the same under every vendor.
  if (parOptionId == null) return no(PAR_TRANSITION_REASONS.PAR_IN_COUNT_UNIT);

  if (newOptionId == null) return no(PAR_TRANSITION_REASONS.NO_PACKAGE_DEFAULT);
  if (String(newOptionId) === String(parOptionId)) {
    return no(PAR_TRANSITION_REASONS.SAME_PACKAGE);
  }

  const displayValue = finitePositiveOrNull(parDisplay?.value);
  // The old package's contents: the stored snapshot when we have it, else
  // implied by the PAR itself (12 bags typed as 1 case = a 12-bag case).
  const oldQty =
    finitePositiveOrNull(parQtyPerPackage)
    ?? (displayValue !== null ? round6(par / displayValue) : null);

  const newQty = finitePositiveOrNull(newQtyPerPackage);
  // The new package has no confirmed size: never guess. The caller shows the
  // repair state and the PAR stays exactly where it is.
  if (newQty === null) return no(PAR_TRANSITION_REASONS.UNRESOLVED_CONVERSION, true);

  // Same amount in a different wrapper (vendor renamed, relabeled, or an
  // identical package from a new vendor): the PAR still reads cleanly, so
  // there is nothing to ask.
  if (oldQty !== null && Math.abs(newQty - oldQty) <= oldQty * EQUIVALENT_TOLERANCE) {
    return no(PAR_TRANSITION_REASONS.EQUIVALENT_PACKAGE);
  }

  const newEquivalentPackages = round6(par / newQty);
  // Preserving the package count means: the same NUMBER of packages the PAR
  // was set as, in the new package — 1 old case becomes 1 new case, 2 old
  // cases become 2 new cases. Without a stored display count, the nearest
  // whole new package stands in.
  const preservePackages =
    displayValue !== null ? displayValue : Math.max(1, Math.round(newEquivalentPackages));
  const preserveNormalizedPar = round6(preservePackages * newQty);
  const changeAmount = round6(preserveNormalizedPar - par);
  const changePercent = round6((preserveNormalizedPar - par) / par);
  const newEquivalentIsWhole =
    Math.abs(newEquivalentPackages - Math.round(newEquivalentPackages)) <= WHOLE_TOLERANCE;

  const transition = {
    oldOptionId: parOptionId,
    newOptionId,
    oldPackageLabel: oldPackageLabel ?? parDisplay?.unit ?? null,
    newPackageLabel: newPackageLabel ?? null,
    oldQtyPerPackage: oldQty,
    newQtyPerPackage: newQty,
    countUnit: countUnit ?? null,
    normalizedPar: par,
    parDisplayValue: displayValue,
    parDisplayUnit: parDisplay?.unit ?? null,
    newEquivalentPackages,
    newEquivalentIsWhole,
    oldParWasExactPackages:
      displayValue !== null
      && Math.abs(displayValue - Math.round(displayValue)) <= WHOLE_TOLERANCE,
    preservePackages,
    preserveNormalizedPar,
    // Back-compat aliases for readers written against the earlier naming.
    rebasePackages: preservePackages,
    rebaseNormalizedPar: preserveNormalizedPar,
    changeAmount,
    changePercent,
  };

  // An exact equivalent — the PAR lands on a whole number of NEW packages —
  // has no stocking question to ask: keeping the quantity is also clean in
  // the new package. No prompt, no AI call; the caller silently refreshes the
  // friendly display (normalized PAR untouched) and records the resolution.
  if (newEquivalentIsWhole) {
    return {
      required: false,
      reason: PAR_TRANSITION_REASONS.EXACT_EQUIVALENT,
      needsReview: false,
      transition,
      autoResolve: true,
    };
  }

  return {
    required: true,
    reason: null,
    needsReview: false,
    transition,
    autoResolve: false,
  };
}

/** The identity a stored transition row is keyed on. */
export function parTransitionKey(inventoryItemId, oldOptionId, newOptionId) {
  return `${inventoryItemId}::${oldOptionId == null ? "" : String(oldOptionId)}::${newOptionId == null ? "" : String(newOptionId)}`;
}

/* ── Recommendation ───────────────────────────────────────────────────────── */

const pct = (fraction) => `${Math.abs(Math.round(fraction * 100))}%`;

const unitLabel = (transition, qty) => {
  const unit = transition.countUnit || "unit";
  return `${round6(qty)} ${unit}${round6(qty) === 1 || String(unit).endsWith("s") ? "" : "s"}`;
};

/**
 * The deterministic PAR-INTENT recommendation, where the safe choice is
 * logically clear — most transitions never cost an API call. This is a
 * different decision from invoice package-language interpretation (which is
 * AI-first; see purchaseOptionSync's interpretation flow): here every number
 * is already a verified fact, and the only question is restaurant intent.
 *
 *   * the PAR is a whole number of new packages → detectParTransition already
 *     auto-resolves it without a prompt; the rule stays here as a server-side
 *     guard for callers that ask anyway;
 *   * preserving the package count would move the stocking target by
 *     PAR_INTENT_AI_THRESHOLD (20%) or more → recommend keeping the stock
 *     level, stated with the consequence. Both choices are still shown and
 *     the package-count option still requires explicit confirmation;
 *   * anything smaller — where package-count intent is plausible — is
 *     genuinely ambiguous and returns null so the caller may ask the AI
 *     (or fall back to keep).
 *
 * The threshold decides ONLY whether the AI is consulted. It never alters
 * the arithmetic, the available choices, or the confirmation requirement.
 *
 * @param {object} transition  from detectParTransition
 * @returns {{recommendation, confidence, reason, facts_used, source}|null}
 */
export function deterministicParRecommendation(transition) {
  if (!transition) return null;

  if (transition.newEquivalentIsWhole) {
    const wholePackages = Math.round(transition.newEquivalentPackages);
    return {
      recommendation: PAR_DECISIONS.KEEP,
      confidence: 0.99,
      reason:
        `Your PAR of ${unitLabel(transition, transition.normalizedPar)} is exactly `
        + `${wholePackages} of the new package, so keeping the quantity changes nothing on the shelf.`,
      facts_used: ["normalized_par", "new_package_size", "new_equivalent_packages"],
      source: "deterministic",
    };
  }

  if (Math.abs(transition.changePercent) >= PAR_INTENT_AI_THRESHOLD) {
    const direction = transition.changeAmount < 0 ? "reduce" : "raise";
    return {
      recommendation: PAR_DECISIONS.KEEP,
      confidence: 0.95,
      reason:
        `Your PAR is ${unitLabel(transition, transition.normalizedPar)}. Using the same number of new packages `
        + `(${transition.preservePackages}) would ${direction} the target to `
        + `${unitLabel(transition, transition.preserveNormalizedPar)} — a ${pct(transition.changePercent)} change `
        + `your kitchen did not ask for.`,
      facts_used: ["normalized_par", "old_package_size", "new_package_size", "preserve_change_percent"],
      source: "deterministic",
    };
  }

  return null;
}

/**
 * The safe answer when the AI is unavailable, times out, or returns garbage:
 * keep the normalized stock level unchanged.
 */
export function fallbackParRecommendation(transition, warning = "ai_unavailable") {
  return {
    recommendation: PAR_DECISIONS.KEEP,
    confidence: 0.5,
    reason:
      transition
        ? `Keep the PAR at ${unitLabel(transition, transition.normalizedPar)} so the package change does not move your stock level.`
        : "Keep the PAR unchanged so the package change does not move your stock level.",
    facts_used: ["normalized_par"],
    source: "fallback",
    warning,
  };
}

/**
 * The structured, verified facts the AI is allowed to see — nothing else.
 * Every quantity here was computed deterministically by the caller; the AI
 * may reason over them but can never restate a conversion (its response
 * schema has no quantity fields).
 */
export function buildParRecommendationFacts({ item, transition, history } = {}) {
  if (!transition) return null;
  return {
    item_name: item?.description ?? item?.name ?? null,
    item_category: item?.category ?? null,
    count_unit: transition.countUnit,
    normalized_par: transition.normalizedPar,
    par_entered_as: transition.parDisplayValue != null
      ? `${transition.parDisplayValue} × ${transition.oldPackageLabel ?? "old package"}`
      : null,
    old_package_label: transition.oldPackageLabel,
    old_package_size: transition.oldQtyPerPackage,
    new_package_label: transition.newPackageLabel,
    new_package_size: transition.newQtyPerPackage,
    new_equivalent_packages: transition.newEquivalentPackages,
    new_equivalent_is_whole: transition.newEquivalentIsWhole,
    old_par_was_exact_packages: transition.oldParWasExactPackages,
    preserve_packages: transition.preservePackages,
    preserve_normalized_par: transition.preserveNormalizedPar,
    preserve_change_amount: transition.changeAmount,
    preserve_change_percent: transition.changePercent,
    recent_package_history: Array.isArray(history?.recentPackages) ? history.recentPackages : undefined,
    prior_preserve_count: Number.isFinite(history?.priorPreserveCount) ? history.priorPreserveCount : undefined,
    prior_keep_count: Number.isFinite(history?.priorKeepCount) ? history.priorKeepCount : undefined,
  };
}
